import { useEffect, useState } from "react";

/**
 * Module-level store of in-flight HTTP requests so non-React code (axios
 * interceptors) can advertise what's blocking the user, and React components
 * (e.g. BootProgress) can render that.
 *
 * Why a hand-rolled store vs Context: the same axios instances are imported in
 * non-React modules (services, helpers) — those can't read Context. A module
 * singleton + subscription is the lightest thing that works for both.
 */

export type InflightRequest = {
  url: string;          // pathname only — query string stripped for privacy
  method: string;       // upper-cased ("GET", "POST", ...)
  startedAt: number;    // performance.now() milliseconds — monotonic, immune to clock skew
};

const requests = new Map<string, InflightRequest>();
const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach((l) => l());
};

const stripQuery = (url: string): string => {
  // Accept absolute and relative URLs. Anything we can't parse we surface raw.
  try {
    const u = new URL(url, "http://_");
    return u.pathname || url;
  } catch {
    const q = url.indexOf("?");
    return q === -1 ? url : url.slice(0, q);
  }
};

export const trackRequestStart = (id: string, url: string, method: string) => {
  requests.set(id, {
    url: stripQuery(url),
    method: method.toUpperCase(),
    startedAt: performance.now(),
  });
  notify();
};

export const trackRequestEnd = (id: string) => {
  if (requests.delete(id)) notify();
};

/**
 * React hook returning the longest-pending in-flight request (or null when
 * nothing's in flight). Re-renders on subscribe events AND on a slow timer so
 * elapsed-time captions update without spawning per-component intervals.
 */
export const useLongestInflightRequest = (
  tickMs = 500,
): InflightRequest | null => {
  const [snapshot, setSnapshot] = useState<InflightRequest | null>(null);

  useEffect(() => {
    const recompute = () => {
      if (requests.size === 0) {
        setSnapshot(null);
        return;
      }
      let longest: InflightRequest | null = null;
      requests.forEach((req) => {
        if (!longest || req.startedAt < longest.startedAt) longest = req;
      });
      setSnapshot(longest);
    };

    listeners.add(recompute);
    const intervalId = window.setInterval(recompute, tickMs);
    recompute();

    return () => {
      listeners.delete(recompute);
      window.clearInterval(intervalId);
    };
  }, [tickMs]);

  return snapshot;
};
